export default {
  id: 'printers',
  title: 'Pisači i integracije',
  icon: 'mdi-printer',
  show: true,
  order: 12,
  children: [
    {
      id: 'printers-overview',
      title: 'Konfiguracija pisača',
      icon: 'mdi-printer-settings',
      content: () => `
        <h2 id="printers-overview">Konfiguracija pisača</h2>
        <p>
          Modul za konfiguraciju pisača omogućava postavljanje fiskalnih i termičkih pisača
          koji se koriste za ispis računa, kuhinjskih narudžbi i izvještaja.
        </p>

        <h3 class="mt-4">Tipovi pisača</h3>
        <table class="info-table">
          <thead>
            <tr>
              <th>Tip</th>
              <th>Namjena</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Fiskalni pisač</strong></td>
              <td>Ispis fiskalnih računa (EPSON, STAR)</td>
            </tr>
            <tr>
              <td><strong>Termički pisač</strong></td>
              <td>Ispis u kuhinju, šanku, kopije računa</td>
            </tr>
            <tr>
              <td><strong>PDF pisač</strong></td>
              <td>Virtualni pisač za spremanje računa u PDF</td>
            </tr>
          </tbody>
        </table>

        <h3 class="mt-4">Dodavanje pisača</h3>
        <ol>
          <li>Idite na <strong>"Postavke" → "Pisači"</strong></li>
          <li>Kliknite <strong>"+ Dodaj pisač"</strong></li>
          <li>Odaberite tip pisača</li>
          <li>Unesite naziv i IP adresu (za mrežne pisače)</li>
          <li>Testirajte vezu</li>
          <li>Spremite konfiguraciju</li>
        </ol>

        <div class="info-box">
          <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
          <div>
            <strong>Važno:</strong> Prije konfiguracije, provjerite da je pisač pravilno povezan
            na mrežu ili USB port računala.
          </div>
        </div>
      `
    },
    {
      id: 'printers-add',
      title: 'Dodavanje pisača',
      icon: 'mdi-plus',
      content: () => `
        <h2 id="add-printer">Dodavanje pisača</h2>
        <p>Dodavanje termičkih ili fiskalnih pisača za ispis računa.</p>

        <h3 class="mt-4">Korak po korak</h3>
        <ol>
          <li>Idite na <strong>"Postavke" → "Pisači i integracije"</strong></li>
          <li>Kliknite <strong>"+ Dodaj pisač"</strong></li>
          <li>Unesite <strong>Naziv pisača</strong> (npr. "Fiskalni pisač - Blagajna 1")</li>
          <li>Odaberite <strong>Tip pisača</strong>:
            <ul>
              <li>EPSON TM-T88</li>
              <li>STAR TSP100</li>
              <li>PDF pisač (virtualni)</li>
            </ul>
          </li>
          <li>Unesite <strong>IP adresu</strong> pisača (npr. 192.168.1.100)</li>
          <li>Odaberite <strong>Port</strong> (default: 9100)</li>
          <li>Testirajte vezu klik om na <strong>"Test ispisa"</strong></li>
          <li>Kliknite <strong>"Spremi"</strong></li>
        </ol>

        <div class="info-box">
          <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
          <div>
            <strong>Mrežni pisači:</strong> Preporučujemo korištenje mrežnih pisača (Ethernet/WiFi) umjesto USB za lakšu konfiguraciju.
          </div>
        </div>

        <h3 class="mt-4">Povezivanje s blagajnom</h3>
        <p>Nakon dodavanja pisača, povežite ga s blagajnom:</p>
        <ol>
          <li>Idite na <strong>"Blagajne" → "Pregled blagajna"</strong></li>
          <li>Odaberite blagajnu i kliknite <strong>"Postavke"</strong></li>
          <li>U sekciji "Pisači" dodajte pisač</li>
          <li>Odaberite tip ispisa (Fiskalni račun, Kopija)</li>
        </ol>
      `
    },
    {
      id: 'printers-types',
      title: 'Tipovi pisača',
      icon: 'mdi-information',
      content: () => `
        <h2 id="printer-types">Tipovi pisača</h2>
        <p>AI Kasa podržava različite tipove pisača za različite potrebe poslovanja.</p>

        <h3 class="mt-4">EPSON fiskalni pisači</h3>
        <p>Najčešće korišteni fiskalni pisači u Hrvatskoj. Podržani modeli:</p>
        <ul>
          <li>EPSON FP-81II</li>
          <li>EPSON FP-90III</li>
          <li>EPSON TM-T88V (sa fiskalnim modulom)</li>
        </ul>

        <h3 class="mt-4">STAR fiskalni pisači</h3>
        <p>Alternativa EPSON pisačima:</p>
        <ul>
          <li>STAR mC-Print3</li>
          <li>STAR TSP650II</li>
        </ul>

        <h3 class="mt-4">Termički pisači (nefiskalni)</h3>
        <p>Za ispis u kuhinju, šank ili kopije računa:</p>
        <ul>
          <li>EPSON TM-m30</li>
          <li>STAR TSP100</li>
          <li>Bilo koji ESC/POS kompatibilni pisač</li>
        </ul>

        <div class="success-box">
          <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
          <div>
            <strong>Savjet:</strong> Za fiskalizaciju u Hrvatskoj obavezno koristite certificirane fiskalne pisače (EPSON ili STAR).
          </div>
        </div>
      `
    },
    {
      id: 'printers-org-units',
      title: 'Organizacijske jedinice',
      icon: 'mdi-domain',
      content: () => `
        <h2 id="org-units">Organizacijske jedinice</h2>
        <p>
          Organizacijske jedinice omogućavaju logičku podjelu pisača prema lokacijama ili funkcijama.
        </p>

        <h3 class="mt-4">Primjeri organizacijskih jedinica</h3>
        <ul>
          <li><strong>Kuhinja</strong> - Pisač za ispis narudžbi u kuhinji</li>
          <li><strong>Šank</strong> - Pisač za ispis pića</li>
          <li><strong>Blagajna 1</strong> - Glavni fiskalni pisač</li>
          <li><strong>Blagajna 2</strong> - Rezervni fiskalni pisač</li>
        </ul>

        <h3 class="mt-4">Konfiguracija</h3>
        <p>Prilikom dodavanja pisača, odaberite organizacijsku jedinicu kojoj pripada.</p>
      `
    },
    {
      id: 'printers-pos',
      title: 'POS uređaji',
      icon: 'mdi-account-credit-card',
      content: () => `
        <h2 id="pos-devices">POS uređaji</h2>
        <p>
          POS uređaji (terminali za plaćanje karticama) mogu biti integrirani s AI Kasa sustavom
          za automatsko procesiranje kartinih plaćanja.
        </p>

        <h3 class="mt-4">Podržani POS terminali</h3>
        <ul>
          <li>Ingenico terminali (iPP320, iWL220)</li>
          <li>Verifone terminali (V400m)</li>
          <li>PAX terminali (A920, A80)</li>
        </ul>

        <h3 class="mt-4">Integracija</h3>
        <p>Za integraciju POS terminala potrebno je:</p>
        <ol>
          <li>Kontaktirati vašu banku za aktivaciju</li>
          <li>Dobiti API ključeve za integraciju</li>
          <li>Konfigurirati terminal u AI Kasa postavkama</li>
        </ol>
      `
    },
    {
      id: 'printers-cards',
      title: 'Kartice (Card Configuration)',
      icon: 'mdi-credit-card',
      content: () => `
        <h2 id="payment-cards">Kartice</h2>
        <p>
          Konfiguracija prihvaćenih platnih kartica i načina plaćanja.
        </p>

        <h3 class="mt-4">Tipovi kartica</h3>
        <table class="info-table">
          <thead>
            <tr>
              <th>Tip kartice</th>
              <th>Primjer</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Debitne kartice</strong></td>
              <td>Maestro, Visa Debit, MasterCard Debit</td>
            </tr>
            <tr>
              <td><strong>Kreditne kartice</strong></td>
              <td>Visa, MasterCard, American Express, Diners</td>
            </tr>
            <tr>
              <td><strong>Beskontaktno plaćanje</strong></td>
              <td>NFC, Apple Pay, Google Pay</td>
            </tr>
            <tr>
              <td><strong>Voucher kartice</strong></td>
              <td>Poklon kartice, kupon kartice</td>
            </tr>
          </tbody>
        </table>

        <h3 class="mt-4">Konfiguracija</h3>
        <p>Označite koje tipove kartica prihvaćate i postavite provizije po vrstama plaćanja.</p>
      `
    }
  ]
}
